import { INBOX_BRIDGE_APP_SLUG, INBOX_BRIDGE_TOKEN_KEY } from './embedBridge.js';

/** The complete embedded-inbox read surface. Paths not listed here never
 * receive the bridge token (see the PX-5 note inside request()). */
export const EMBED_REWRITES: ReadonlyArray<[RegExp, string]> = [
  [/^\/api\/auth\/me(\?|$)/, '/api/embed/inbox/me$1'],
  [/^\/api\/deal-messages\/inbox(\?|$)/, '/api/embed/inbox/messages$1'],
  [/^\/api\/deal-messages\/counts(\?|$)/, '/api/embed/inbox/counts$1'],
  [/^\/api\/deal-messages\/inbox\/deal-parties(\?|$)/, '/api/embed/inbox/deal-parties$1'],
  // The reading pane. Without this the LIST renders embedded but OPENING a
  // message falls through to the ordinary cookie-authenticated route. That
  // only appeared to work because landally.ai and outreach.landally.ai share a
  // registrable domain, making the frame same-site so a SameSite=lax cookie is
  // still sent; any browser that partitions embedded-frame cookies 401s here.
  [/^\/api\/deal-messages\/([^/?#]+)\/context(\?|$)/, '/api/embed/inbox/messages/$1/context$2'],
  [/^\/api\/connected-accounts(\?|$)/, '/api/embed/inbox/connected-accounts$1'],
  [/^\/api\/tags(\?|$)/, '/api/embed/inbox/tags$1'],
  [/^\/api\/deals(\?|$)/, '/api/embed/inbox/deals$1'],
  [/^\/api\/deals\/([^/?#]+)\/parties(\?|$)/, '/api/embed/inbox/deals/$1/parties$2'],
  [/^\/api\/deals\/([^/?#]+)\/file-tree(\?|$)/, '/api/embed/inbox/deals/$1/file-tree$2'],
];

/**
 * True only when this document is genuinely running inside a parent frame.
 *
 * The rewrites below used to fire on the mere PRESENCE of a bridge token in
 * sessionStorage. A token left behind by viewing the embedded inbox in LA-AI
 * then kept capturing that tab when POE was browsed directly afterwards, and
 * once the token aged out (15 minutes) every rewritten path answered 401.
 * Because `/api/auth/me` is one of them, the app concluded there was no
 * session and bounced a perfectly healthy login to /login.
 *
 * Being framed is the honest test for "am I embedded", and it cannot be left
 * behind in storage. Cross-origin access to `window.top` throws, and that throw
 * is itself proof of a parent, so it resolves to embedded rather than to safe.
 */
function isActuallyEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/** Drop a bridge token the server has just rejected, so the tab self-heals. */
function discardBridgeToken(): void {
  try {
    window.sessionStorage.removeItem(INBOX_BRIDGE_TOKEN_KEY);
  } catch {
    // Storage can be unavailable; nothing to clean up in that case.
  }
}

/**
 * Typed API client for POE.
 * All requests go through this module for consistent error handling.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  url: string,
  options: RequestInit = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  // PX-5: in embedded mode, ONLY requests the enumerated bridge surface can
  // serve are translated onto /api/embed/inbox/* and carry the token.
  // Lender delivery has two additional method-specific routes. Its POST is
  // authorized server-side with inbox:write; inbox:read alone cannot send.
  // All other requests retain ordinary session auth, without a bridge token.
  let requestUrl = url;
  let usedBridgeToken = false;
  if (typeof window !== 'undefined' && isActuallyEmbedded()) {
    const bridgeToken = window.sessionStorage.getItem(INBOX_BRIDGE_TOKEN_KEY);
    if (bridgeToken && !headers.has('Authorization')) {
      const method = (options.method || 'GET').toUpperCase();
      const lenderSuffix = method === 'GET' ? 'options' : method === 'POST' ? 'messages' : null;
      const lenderPattern = lenderSuffix
        ? new RegExp(`^/api/llc/lender-conversations/([^/?#]+)/([^/?#]+)/${lenderSuffix}(\\?|$)`)
        : null;
      const rewrite: [RegExp, string] | undefined = lenderPattern?.test(url)
        ? [lenderPattern, `/api/embed/inbox/llc/lender-conversations/$1/$2/${lenderSuffix}$3`]
        : (method === 'GET' || method === 'HEAD')
          ? EMBED_REWRITES.find(([pattern]) => pattern.test(url))
          : undefined;
      if (rewrite) {
        requestUrl = url.replace(rewrite[0], rewrite[1]);
        headers.set('Authorization', `Bearer ${bridgeToken}`);
        headers.set('X-POE-Embedded', INBOX_BRIDGE_APP_SLUG);
        usedBridgeToken = true;
      }
    }
  }

  const res = await fetch(requestUrl, {
    ...options,
    headers,
    credentials: 'include',
  });

  // An expired bridge token would otherwise be retried forever. Dropping it on
  // the first rejection lets the next request fall back to the normal session
  // path instead of stranding the tab.
  if (res.status === 401 && usedBridgeToken) {
    discardBridgeToken();
  }

  if (!res.ok) {
    let errorData: { error?: string } = {};
    try {
      errorData = await res.json();
    } catch {
      // ignore parse error
    }
    throw new ApiError(
      errorData.error || `HTTP ${res.status}`,
      res.status,
      errorData,
    );
  }

  if (res.status === 204) return undefined as T;

  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(url: string, options?: RequestInit) =>
    request<T>(url, { ...options, method: 'GET' }),

  post: <T>(url: string, body?: unknown, options?: RequestInit) =>
    request<T>(url, {
      ...options,
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),

  patch: <T>(url: string, body?: unknown, options?: RequestInit) =>
    request<T>(url, {
      ...options,
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),

  put: <T>(url: string, body?: unknown, options?: RequestInit) =>
    request<T>(url, {
      ...options,
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(url: string, body?: unknown, options?: RequestInit) =>
    request<T>(url, {
      ...options,
      method: 'DELETE',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
};

// Query key factories
export const queryKeys = {
  auth: {
    me: () => ['auth', 'me'] as const,
  },
  tenant: {
    me: () => ['tenant', 'me'] as const,
  },
  deals: {
    list: (params?: Record<string, unknown>) => ['deals', 'list', params] as const,
    detail: (id: string) => ['deals', 'detail', id] as const,
    activity: (id: string) => ['deals', 'activity', id] as const,
    followers: (id: string) => ['deals', 'followers', id] as const,
  },
  users: {
    list: () => ['users', 'list'] as const,
  },
  crossApp: {
    laAiDealUrl: (dealThreadId: string) => ['cross-app', 'la-ai-url', dealThreadId] as const,
  },
  dealParties: {
    list: (dealThreadId: string) => ['deal-parties', 'list', dealThreadId] as const,
  },
  referenceContacts: {
    suggestions: (dealThreadId: string, type?: string, state?: string | null, county?: string | null) =>
      ['reference-contacts', 'suggestions', dealThreadId, type ?? 'all', state ?? null, county ?? null] as const,
  },
  dealMessages: {
    list: (dealThreadId: string, params?: Record<string, unknown>) =>
      ['deal-messages', 'list', dealThreadId, params] as const,
  },
  calendar: {
    list: (params?: Record<string, unknown>) => ['calendar', 'list', params] as const,
  },
  connectedAccounts: {
    list: () => ['connected-accounts', 'list'] as const,
  },
  voiceProfiles: {
    list: () => ['voice-profiles', 'list'] as const,
    detail: (id: string) => ['voice-profiles', 'detail', id] as const,
  },
  employeeChat: {
    conversations: (employeeId?: string) => ['employee-chat', 'conversations', employeeId ?? 'all'] as const,
    messages: (conversationId: string) => ['employee-chat', 'messages', conversationId] as const,
  },
  notes: {
    list: (params?: Record<string, unknown>) => ['notes', 'list', params] as const,
  },
  batches: {
    list: () => ['batches', 'list'] as const,
    detail: (id: string) => ['batches', 'detail', id] as const,
    cards: (id: string) => ['batches', 'cards', id] as const,
  },
  savedViews: {
    list: (scope?: string) => ['saved-views', 'list', scope] as const,
  },
  calendarEvents: {
    list: (params?: Record<string, unknown>) => ['calendar-events', 'list', params] as const,
  },
  listPrefs: {
    get: (scope: string) => ['list-prefs', scope] as const,
  },
  filterPrefs: {
    get: (scope: string) => ['filter-prefs', scope] as const,
  },
};
